import dayjs from 'dayjs';

import { TimeSeriesDataPoint } from '../../Data/mockDataGenerator';

export interface TimeRange {
    days: number;
    format: string;
    hours: number;
    isHourly: boolean;
}

export interface SummarizedDataPoint {
    average: number;
    max: number;
    min: number;
    timestamp: Date;
}

/**
 * Returns the friendly display text for a timeline selection
 */
export function getTimeRangeLabel(timeline: string): string {
    switch (timeline) {
        case 'today':
            return 'past 24 hours';
        case '3days':
            return 'last 3 days';
        case 'week':
            return 'last week';
        case 'month':
            return 'last month';
        case '3months':
            return 'last 3 months';
        case '6months':
            return 'last 6 months';
        case 'year':
            return 'last year';
        default:
            return 'selected period';
    }
}

/**
 * Filters time series data based on the selected time range
 */
export function filterTimeSeriesData(data: TimeSeriesDataPoint[], timeRange: TimeRange): TimeSeriesDataPoint[] {
    const now = dayjs();
    return data.filter((point) => {
        const pointDate = dayjs(point.timestamp);
        if (timeRange.isHourly) {
            return pointDate.isAfter(now.subtract(timeRange.hours, 'hour'));
        }
        return pointDate.isAfter(now.subtract(timeRange.days, 'day'));
    });
}

/**
 * Filters summarized data based on the selected time range
 */
export function filterSummarizedData(data: SummarizedDataPoint[], timeRange: TimeRange): SummarizedDataPoint[] {
    const now = dayjs();
    return data.filter((point) => {
        const pointDate = dayjs(point.timestamp);
        if (timeRange.isHourly) {
            return pointDate.isAfter(now.subtract(timeRange.hours, 'hour'));
        }
        return pointDate.isAfter(now.subtract(timeRange.days, 'day'));
    });
}

/**
 * Generates appropriate axis labels based on the time range
 */
export function generateTimeLabels(
    timeRange: TimeRange,
    useSummarizedData: boolean,
    summarizedData: SummarizedDataPoint[],
): string[] {
    const { days, format, isHourly } = timeRange;

    if (useSummarizedData) {
        return summarizedData.map((point) => dayjs(point.timestamp).format(format));
    }

    if (isHourly) {
        const now = dayjs();
        return Array.from({ length: 24 }, (_, i) => {
            return now.subtract(23 - i, 'hour').format('HH:mm');
        });
    }

    return Array.from({ length: days }, (_, i) => {
        return dayjs()
            .subtract(days - 1 - i, 'day')
            .format(format);
    });
}

/**
 * Calculates daily or hourly data averages for bar/line charts
 */
export function calculateAverageData(
    timeSeriesData: TimeSeriesDataPoint[],
    summarizedData: SummarizedDataPoint[],
    timeRange: TimeRange,
    useSummarizedData: boolean,
): number[] {
    if (useSummarizedData) {
        return summarizedData.map((point) => point.average);
    }

    // For "today" option (past 24 hours), group data by hour
    if (timeRange.isHourly) {
        const hourlyData = Array(24).fill(0);
        const hourlyCount = Array(24).fill(0);
        const now = dayjs();

        // Group data by hour
        timeSeriesData.forEach((point) => {
            const pointDate = dayjs(point.timestamp);
            const hoursAgo = now.diff(pointDate, 'hour');

            // Only include data from the last 24 hours
            if (hoursAgo >= 0 && hoursAgo < 24) {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                hourlyData[hoursAgo] += point.metric;
                hourlyCount[hoursAgo]++;
            }
        });

        // Calculate hourly averages
        return hourlyData
            .map((sum, i) => (hourlyCount[i] > 0 ? Number.parseFloat((sum / hourlyCount[i]).toFixed(1)) : 0))
            .reverse(); // Reverse to show oldest first
    }

    // For other time ranges, group by day as before
    const { days } = timeRange;
    return Array.from({ length: days }, (_, i) => {
        const dayData = timeSeriesData.filter((point) =>
            dayjs(point.timestamp).isSame(dayjs().subtract(days - 1 - i, 'day'), 'day'),
        );

        return dayData.length > 0
            ? Number.parseFloat((dayData.reduce((sum, point) => sum + point.metric, 0) / dayData.length).toFixed(1))
            : 0;
    });
}

/**
 * Converts time series data into scatter plot points
 */
export function convertToScatterData(
    timeSeriesData: TimeSeriesDataPoint[],
    summarizedData: SummarizedDataPoint[],
    weekdayPatternData: TimeSeriesDataPoint[],
    useSummarizedData: boolean,
): { id: number; x: number; y: number }[] {
    const dataSource = useSummarizedData
        ? summarizedData.map((d) => ({
              metric: d.average,
              timestamp: d.timestamp,
          }))
        : timeSeriesData;

    // Always limit scatter plot points for performance
    const maxPoints = 100;
    const step = Math.max(1, Math.floor(dataSource.length / maxPoints));

    // Create (x,y) pairs from the two data series
    return dataSource
        .filter((_, index) => index % step === 0)
        .slice(0, maxPoints)
        .map((point, index) => ({
            id: index,
            x: point.metric,
            y: index < weekdayPatternData.length ? (weekdayPatternData[index]?.metric ?? 50) : 50 + Math.random() * 25,
        }));
}

/**
 * Creates pie chart data categories from time series data
 */
export function createPieChartData(
    timeSeriesData: TimeSeriesDataPoint[],
    summarizedData: SummarizedDataPoint[],
    useSummarizedData: boolean,
): { id: number; label: string; value: number }[] {
    const dataSource = useSummarizedData
        ? summarizedData.map((d) => ({
              metric: d.average,
              timestamp: d.timestamp,
          }))
        : timeSeriesData;

    // Create 6 sections based on value ranges with office location names
    const veryLow = dataSource.filter((point) => point.metric < 20).length;
    const low = dataSource.filter((point) => point.metric >= 20 && point.metric < 40).length;
    const mediumLow = dataSource.filter((point) => point.metric >= 40 && point.metric < 60).length;
    const mediumHigh = dataSource.filter((point) => point.metric >= 60 && point.metric < 75).length;
    const high = dataSource.filter((point) => point.metric >= 75 && point.metric < 90).length;
    const veryHigh = dataSource.filter((point) => point.metric >= 90).length;

    return [
        { id: 0, label: 'Kitchen', value: veryLow },
        { id: 1, label: 'Conference Room', value: low },
        { id: 2, label: 'Reception', value: mediumLow },
        { id: 3, label: 'Open Space', value: mediumHigh },
        { id: 4, label: 'Break Room', value: high },
        { id: 5, label: 'Server Room', value: veryHigh },
    ];
}

/**
 * Calculates the average value for gauge charts
 */
export function calculateGaugeValue(
    timeSeriesData: TimeSeriesDataPoint[],
    summarizedData: SummarizedDataPoint[],
    useSummarizedData: boolean,
): number {
    if (useSummarizedData) {
        return summarizedData.length > 0
            ? summarizedData.reduce((sum, point) => sum + point.average, 0) / summarizedData.length
            : 0;
    }

    return timeSeriesData.length > 0
        ? timeSeriesData.reduce((sum, point) => sum + point.metric, 0) / timeSeriesData.length
        : 0;
}
